import React from 'react';
import {
  fetchRegionFeatured,
  fetchRegionFeaturedCam,
  fetchRegionMeta, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus, fetchRegionStats,
  fetchRegionResortListGuide,
} from 'api/region/static';
import {
  fetchResortDescriptionTabs,
  fetchResortSnowReport,
} from 'api/resort/static';
import {
  IRegionCam, IRegionResortsPerStatus, IRegionShort, IRegionStats,
  IRegionFeaturedResort,
} from 'api/region/types';
import {
  IRelatedNews, OpenFlag, IResortPathInfo, IDescriptionsResort, IResortPageSnowInfo, ResortStatus,
} from 'api/resort/types';
import getDailyFeaturedResortIndex from 'util/getDailyFeaturedResortIndex';
import { IFeaturedResort } from 'api/home/types';
import { AxiosResponse } from 'axios';
import { Hero400Error } from 'components/400';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { NextLinkTr } from 'components/blocks/NextLink';
import { FeaturedCamera } from 'components/common/FeaturedCamera';
import { FeaturedResort } from 'components/home/FeaturedResort';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { WeatherReports } from 'components/region/WeatherReports';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { RegularAd } from 'googlead/components/RegularAd';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { Routes } from 'routes';

import { getRegionSlug } from 'util/memcache';
import cameraStyles from 'components/common/FeaturedCamera/styles.module.scss';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { formatPercentVal } from 'util/formatPercent';
import { MIN_ROWS_DISPLAY_AD } from './constants';

export type RegionSkiReportPageProps = {
  resorts: Partial<IRegionResortsPerStatus>,
  featured: IRegionFeaturedResort,
  featuredResortByIndex: IResortPageSnowInfo | null,
  featuredCam: IRegionCam,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  stat: IRegionStats,
  regionResortListGuide: Array<IResortPathInfo>,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-skireport-page';
export const TEMPLATE_ID = '2.1.1';
export const TEMPLATE_NAME = 'Region Snow Report';

const flagsOrder = [OpenFlag.Open, OpenFlag.WeekendsOnly, OpenFlag.TemporarilyClosed, OpenFlag.Closed, OpenFlag.NoReportAvailable, OpenFlag.PermanentlyClosed];

export default function RegionSkiReportPage({
  resorts, pathInfo, featuredCam, numResorts,
  featuredResortByIndex,
  relatedNews,
  stat,
  regionResortListGuide,
  featuredResortDescriptionTabs,
}: RegionSkiReportPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  useRegionLayoutBubble({});

  if (!resorts) {
    return <Hero400Error light />;
  }

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  const seoTitle = pathInfo.isLogical ? t('region.skiReport.logicSeoTitle', { region: pathInfo.title })
    : t('region.skiReport.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.skiReport.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    percent_depth_avg: formatPercentVal(stat.depthsPercent.base, ''),
    avgsnowfall: dt(stat.averageSnowfallCm),
  })
    : m('region.skiReport.seoText', {
      region: pathInfo.title,
      numresorts: numResorts,
      percent_depth_avg: formatPercentVal(stat.depthsPercent.base, ''),
      avgsnowfall: dt(stat.averageSnowfallCm),
    });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle}>
            <NextLinkTr href={Routes.regionCams(pathInfo)}>
              {t('region.skiReport.webcamSnowReport')}
            </NextLinkTr>
          </BlockHeader>
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {flagsOrder
          .filter((flag) => !!resorts[flag]?.data.length)
          .map((flag, index) => (
            <React.Fragment key={flag}>
              <WeatherReports
                regionId={pathInfo.uuid}
                resorts={resorts[flag]!}
                openFlag={flag}
                isFresh
              />
              {resorts[flag]!.data.length > MIN_ROWS_DISPLAY_AD && (
                <RegularAd isFirst={index === 0} />
              )}
            </React.Fragment>
          ))}
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={pathInfo.title}
          margin={{ marginTop: '0rem', marginBottom: '1.75rem' }}
        />
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
        {featuredResort && (
          <FeaturedResort
            resort={featuredResort}
            regionResorts={regionResortListGuide}
            resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
            isOnRegionExceptSkiResorts
            utmCampaign="region_widget"
          />
        )}
      </Main>
      <Side>
        {featuredCam && (
          <FeaturedCamera
            cam={featuredCam.webcam}
            title={featuredCam.resort.title}
            href={Routes.resortCams(featuredCam.resort)}
            isFresh
            className={cameraStyles.sideView}
          />
        )}
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} isRegion hideWeSkiOnMobile />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionSkiReportPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);
  const featuredCam = await fetchRegionFeaturedCam(uuid);

  const partnerResortsResponse = await fetchRegionResortsByStatus(
    uuid,
    ResortStatus.partner,
    1,
    100,
  );
  const partnerResorts = partnerResortsResponse?.data ?? [];

  let featuredResortByIndex: IResortPageSnowInfo | null = null;
  if (partnerResorts.length === 1) {
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[0].uuid);
  } else if (partnerResorts.length > 1) {
    const dailyIndex = getDailyFeaturedResortIndex(partnerResorts.length);
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[dailyIndex].uuid);
  }

  return {
    type: 'region',
    route: 'skireport',
    resorts: {
      [OpenFlag.Open]: await fetchRegionResortsByStatus(uuid, OpenFlag.Open, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.WeekendsOnly]: await fetchRegionResortsByStatus(uuid, OpenFlag.WeekendsOnly, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.PermanentlyClosed]: await fetchRegionResortsByStatus(uuid, OpenFlag.PermanentlyClosed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.Closed]: await fetchRegionResortsByStatus(uuid, OpenFlag.Closed, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.NoReportAvailable]: await fetchRegionResortsByStatus(uuid, OpenFlag.NoReportAvailable, 0, WEATHER_PAGE_SIZE),
      [OpenFlag.TemporarilyClosed]: await fetchRegionResortsByStatus(uuid, OpenFlag.TemporarilyClosed, 0, WEATHER_PAGE_SIZE),
    },
    featured,
    featuredResortByIndex,
    featuredCam,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),
    stat: await fetchRegionStats(uuid),
    regionResortListGuide: await fetchRegionResortListGuide(uuid),
    featuredResortDescriptionTabs: featuredResortByIndex?.uuid
      ? await fetchResortDescriptionTabs(featuredResortByIndex.uuid.toString())
      : null,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.SNOW,
      link: CurrentLink.SNOW_REPORT,
    },
    adTKey: 'metaregion skireport',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: cache.data.isLogical ? 'region.skiReport.logicHeroTitle' : 'region.skiReport.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.skireport.description',
      title: 'MetaRegion.skireport.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionSkiReportPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionSkiReportPageServerSideProps = wrapServerSideProps(fetchProps);
